export const chromosomeDefects2Module = {
  id: "chromosome-defects-2",
  title: "Chromosomal Defects: Part 2",
  sections: [
    {
      type: "text",
      title: "Turner Syndrome (45,X)",
      content: `
        <h3>Turner Syndrome (45,X or monosomy X)</h3>
          <p><strong>Genetics</strong></p>
            <p>Monosomy X (45,X): one sex chromosome is absent or structurally abnormal. </p>
          
          <ul>
            <li><b>Prevalence:</b> ~1 in 2,000–2,500 live female births</li>
            <li><b>Karyotypes:</b>
            <ul> <li>45,X (~50% of live-born cases)</li> <li>45,X/46,XX mosaicism (~30–40%, the most
             common mosaic form, generally milder)</li> <li>Structural X abnormalities (~10–15%) including 
             isochromosome Xq (i(Xq), duplication of the long arm, loss of the short arm), ring chromosome X, or Xp deletion</li>
            </ul>
             <li>The single X is of <strong>maternal origin in ~80% of cases</strong> (the paternal sex chromosome is most often the one that is lost), due to a higher rate of errors in paternal meiosis II</li>
          </ul>
      
          <p><strong>Clinical features</strong></p>
          <ul>
            <li><span class="purple-term">Short stature</span>: Due to haploinsufficiency of the <em>SHOX</em> gene on the short arm of the X chromosome (also present on Y → normally two copies; Turner has only one). SHOX encodes a homeobox transcription factor essential for long bone growth. Growth hormone therapy from early childhood improves final adult height significantly</li>
            <li><span class="purple-term">Webbed neck (pterygium colli)</span>: Caused by cystic hygroma (dilated lymphatic channels) in utero → the hygroma resolves but leaves posterior neck webbing and low posterior hairline. Also causes oedema of hands and feet at birth (neonatal lymphoedema — an important early clue)</li>
            <li><b class="purple-term">Shield chest</b> with widely spaced nipples</li> 
            <li>cubitus valgus (increased carrying angle)</li>
            <li>short 4th metacarpal (50%)</li>
            <li>nail dysplasia (nails are hypoplastic or absent)</li>
            <li><span class="purple-term">Cardiac defects (30–50%)</span>: Bicuspid aortic valve (~30% → most common; risk of aortic stenosis and endocarditis), coarctation of the aorta (~10–15% → associated with webbed neck; presents with radio-femoral delay, hypertension in upper limbs). Aortic dilatation and dissection risk even without bicuspid valve → lifelong cardiac surveillance with MRI</li>
            <li><span class="purple-term">Renal anomalies (~30–50%)</span>: Horseshoe kidney (most common), duplicated collecting system, rotational anomalies → baseline renal ultrasound on diagnosis</li>
            <li><span class="purple-term">Ovarian dysgenesis ('streak ovaries')</span>: Gonads replaced by fibrous stroma → no follicles remaining, and no secretion of sex hormones (estrogen & progesterone). Causes: primary amenorrhoea (no menarche), absent pubertal development, infertility, early osteoporosis. Secondary to streak ovaries: ↓oestradiol and progesterone → ↑LH and FSH (hypergonadotrophic hypogonadism). In ~5–10%, sufficient ovarian development occurs for spontaneous puberty and, rarely, fertility</li>
            <li>Hypothyroidism (15–30% → autoimmune thyroiditis) </li>
            <li>Hearing loss (sensorineural and conductive, due to recurrent otitis media and structural abnormalities of the ear)</li>
            <li>Normal intelligence (IQ within normal range), though specific difficulties in visuospatial tasks and non-verbal processing are common</li>
          </ul>

          <p><strong>Diagnosis and management</strong></p>
          <ul>
            <li><b>Diagnosis:</b> 
            <ul>
            <li><b>Prenatal:</b> nuchal translucency ↑ on USS + <span class="glossary-term">non-invasive prenatal testing (NIPT)<span class="tooltip">A screening test that analyzes small fragments of fetal DNA circulating in the mother's blood.</span></span> → diagnostic karyotype via <span class="glossary-term">CVS/amniocentesis<span class="tooltip">Chorionic villus sampling (CVS) or amniocentesis are invasive procedures to obtain fetal cells for karyotyping.</span></span></li>
            <li><b>Karyotype</b> (blood or buccal cells → at least 30 cells to detect mosaicism)</li> 
            <li><b>Chromosomal microarray</b></li>
             </ul>
            <li><b>Management:</b>
            <ul>
            <li><b class="purple-term">Growth hormone therapy</b> (improves height)</li>
            <li><b class="purple-term">Oestrogen replacement</b> at ~11–13 years (initiates pubertal development, maintains bone density, cardiovascular protection — continue until average age of menopause ~50 years)</li>
            <li><b class="purple-term">Fertility treatment</b> (in vitro fertilisation with donor oocytes — pregnancy possible but carries elevated cardiovascular risk; cardiac evaluation mandatory before attempting pregnancy)</li>
            <li><b class="purple-term">Annual cardiac MRI</b> (aortic diameter monitoring)</li>
            <li><b class="purple-term">Thyroid function tests</b> annually</li>
            <li><b class="purple-term">DEXA scan</b> (bone density), as less estrogen can lead to early osteoporosis</li>
            <li><b class="purple-term">Psychological support</b></li>
          </ul>  </ul>         
      `,
      image: "/images/genetics/turnersyndrome1.png",
    },
    {
      type: "question",
      text: "What is the most common parental origin of the single X chromosome in Turner syndrome?",
      options: [
        "Paternal origin (the X chromosome from the father is retained)",
        "Maternal origin (the X chromosome from the mother is retained)",
        "Equal likelihood of maternal or paternal origin",
        "It depends on the specific karyotype of Turner syndrome",
      ],
      correct: 1,
      explanation:
        "In Turner syndrome, the single X chromosome is of maternal origin in approximately 80% of cases. This means that the paternal sex chromosome is most often the one that is lost during the formation of the gametes. The higher rate of errors in paternal meiosis II contributes to this phenomenon. Therefore, the most common parental origin of the single X chromosome in Turner syndrome is maternal.",
    },
    {
      type: "question",
      text: "What is the underlying genetic cause of short stature in Turner syndrome?",
      options: [
        "Haploinsufficiency of the GH1 gene on chromosome 17",
        "Haploinsufficiency of the IGF1 gene on chromosome 12",
        "Haploinsufficiency of the FGFR3 gene on chromosome 4",
        "Haploinsufficiency of the SHOX gene on the X chromosome",
      ],
      correct: 3,
      explanation:
        "Short stature in Turner syndrome is primarily due to haploinsufficiency of the SHOX gene on the X chromosome. The SHOX gene plays a crucial role in bone growth and development, and having only one functional copy leads to the characteristic short stature seen in affected individuals.",
    },
    {
      type: "question",
      text: "Which of the following cardiac defects is most commonly associated with Turner syndrome?",
      options: [
        "Ventricular septal defect (VSD)",
        "Atrial septal defect (ASD)",
        "Bicuspid aortic valve",
        "Tetralogy of Fallot (TOF)",
      ],
      correct: 2,
      explanation:
        "The most common cardiac defect associated with Turner syndrome is a bicuspid aortic valve, which occurs in approximately 30% of affected individuals.",
    },
    {
      type: "question",
      text: "Which of the following is a common renal anomaly seen in Turner syndrome?",
      options: [
        "Polycystic kidney disease",
        "Renal agenesis",
        "Duplicated collecting system",
        "Horseshoe kidney",
      ],
      correct: 3,
      explanation:
        "Horseshoe kidney is a common renal anomaly seen in Turner syndrome, occurring in approximately 30% of affected individuals. It results from the fusion of the lower poles of the kidneys during embryonic development.",
    },
    {
      type: "question",
      text: "What is the typical hormonal profile in Turner syndrome due to ovarian dysgenesis?",
      options: [
        "Low LH and FSH, high estrogen",
        "High LH and FSH, low estrogen",
        "Low LH and FSH, low estrogen",
        "High LH and FSH, high estrogen",
      ],
      correct: 1,
      explanation:
        "The typical hormonal profile in Turner syndrome due to ovarian dysgenesis is characterized by high levels of LH and FSH and low levels of estrogen. This is because the ovaries are underdeveloped and fail to produce sufficient estrogen, leading to a lack of negative feedback on the hypothalamus and pituitary, resulting in elevated gonadotropins.",
    },
    {
      type: "question",
      text: "Why is it important to perform a cardiac MRI evaluation regularly for individuals with Turner syndrome if they have no congenital heart defects?",
      options: [
        "To rule out latent cardiac anomalies",
        "To monitor for potential development of hypertension",
        "To assess for aortic root dilation",
        "All of the above",
      ],
      correct: 2,
      explanation:
        "Even if individuals with Turner syndrome do not have congenital heart defects, they are at increased risk for aortic root dilation and aortic dissection. Regular cardiac evaluation is important to monitor for these potential complications and to manage them proactively.",
    },
    {
      type: "question",
      text: "Why do individuals with Turner syndrome have webbed neck (pterygium colli)?",
      options: [
        "Due to excessive skin growth during fetal development",
        "Due to a genetic mutation affecting skin elasticity",
        "Due to cystic hygroma in utero leading to lymphatic obstruction",
        "Due to abnormal development of the cervical vertebrae",
      ],
      correct: 2,
      explanation:
        "Individuals with Turner syndrome have a webbed neck (pterygium colli) due to cystic hygroma in utero, which is a lymphatic malformation that leads to the accumulation of fluid and subsequent swelling in the neck region. As the cystic hygroma resolves, it leaves behind the characteristic webbing of the neck seen in Turner syndrome.",
    },
    {
      type: "text",
      title: "Klinefelter Syndrome (47,XXY)",
      content: `
       <h3>Klinefelter Syndrome (47,XXY)</h3>
          <p><strong>Genetics</strong></p>
          <ul>
          <li><b>Prevalence:</b>  1 in 500–1,000 male births. Most common sex chromosome aneuploidy in males.</li>
          <li><b>Karyotypes:</b> 
          <ul>
          <li>47,XXY (classic form, ~80% of cases)</li>
          <ul><li>Approximately 40% arise from maternal nondisjunction in meiosis I, 40% from maternal meiosis II (maternal age effect), and 20% from paternal nondisjunction. </li>
           </ul>
          <li>mosaicism (46,XY/47,XXY, ~10%), and tends to produce a milder phenotype</li> 
          <li>structural abnormalities of the X chromosome (e.g., isochromosome Xq, ring X)</li>         
          </ul></ul>

          <p class="highlight-box">
            The extra X is subject to X-inactivation (<b>Barr body present</b>) but ~15–25% of X-linked genes escape inactivation — dosage effects from these escape genes contribute to the phenotype.
          </p>
</br>
          <p><strong>Clinical features</strong></p>
           <p>Often undiagnosed until investigation of infertility. Most males with Klinefelter syndrome are undiagnosed in childhood. Presentation may be delayed puberty, small testes, gynaecomastia, or tall stature</p>
           
          <ul>
            <li><span class="purple-term">Tall stature with long limbs</span>: Eunuchoid body proportions (arm span exceeds height, lower body segment longer than upper). Broad hips. This occurs because <b>delayed epiphyseal closure</b> from testosterone deficiency allows continued long-bone growth</li>
            <li><span class="purple-term">Hypogonadism</span>: Small, firm testes (testicular volume typically &lt;4 mL vs normal &gt;15 mL). 
            <b>Mechanism:</b> <ul>
            <li>During puberty, seminiferous tubules undergo progressive fibrosis → destruction of <span class="glossary-term">Sertoli cells<span class="tooltip">facilitate spermatogenesis and support developing sperm cells by providing structural and nutritional support</span></span> → loss of inhibin B → ↑FSH. </li>
            <li>Simultaneously, <span class="glossary-term">Leydig cells<span class="tooltip">produce testosterone in response to luteinizing hormone, essential in male sexual development and spermatogenesis</span></span> dysfunction (driven by ↑LH) → reduced testosterone and testicular atrophy → ↑LH further (no testosterone negative feedback on the pituitary gland) → stimulates production of estrogen. </li>
            <li>Testosterone is also peripherally aromatised to oestradiol (predominantly in adipose tissue) → contributes to gynaecomastia and feminine fat distribution. The primary defect is thought to be an abnormal androgen receptor–testosterone interaction, not simply elevated aromatase</li>
            </ul>
            <li><b class="purple-term">Gynaecomastia (bilateral)</b>; sparse facial, body, and pubic hair; micropenis (due to inadequate testosterone exposure in utero)</li>
            <li><b class="purple-term">Cardiovascular risks:</b> 
            <ul><li><b>Venous thromboembolism</b> (↑risk — independent of testosterone levels, seems to stem from hormonal imbalance (high estrogen, low testosterone) and high levels of certain clotting factors); </li>
             <li>modestly increased risk of <b>congenital heart defects</b> (~15-20%). </li>
             <li><b>Varicose veins, due to hormonal imbalance and venous insufficiency</b></li>
            </ul>
            <li><b>Neurodevelopmental</b>: IQ generally within normal limits but approximately 10–15 points below unaffected siblings. Specific difficulties with language-based learning, reading, and expressive language. Increased risk of anxiety, depression, and autism spectrum features. Higher rates of ADHD</li>
            <li><b>Increased risk of</b>: extragonadal germ cell tumours (mediastinal), breast cancer (~20× general male risk — still rare in absolute terms), systemic lupus erythematosus</li>
          </ul>


          <p><strong>Fertility and management</strong></p>
          <ul>
            <li><span class="purple-term">Fertility</span>: Azoospermia in ejaculate is the rule in adulthood. However, focal areas of spermatogenesis may persist — approximately 40–70% of Klinefelter men have retrievable sperm via <span class="glossary-term">testicular sperm extraction (TESE)<span class="tooltip">technique where a needle is used to extract sperm directly from the testes</span></span>, enabling biological fatherhood via <span class="glossary-term">intracytoplasmic sperm injection (ICSI)<span class="tooltip">technique where a single sperm is injected directly into an egg</span></span>. Success rates are higher in younger men (before advancing tubular fibrosis). Fertility preservation (TESE at adolescence before complete tubular destruction) should be offered and discussed early</li>
            <li><b>Diagnosis:</b> Karyotype (at least 30 cells to detect mosaicism) or microarray. Consider in any male with unexplained azoospermia, small firm testes, or gynaecomastia</li>
            <li><b>Management:</b> 
            <ul><li><b class="purple-term">Testosterone replacement therapy</b>(TRT: indicated when testosterone deficient; prevents osteoporosis, improves energy, mood, muscle mass, sexual function; does NOT restore fertility; start timing individualised)</li>
            <li>neuropsychological assessment</li>
            <li><b class="purple-term">Fertility counselling</b> (the sooner TESE is performed, the higher the chances of retrieving sperm)</li>
            <li>psychological support, some studies suggest these individuals are at higher risk of anxiety, depression, and autism spectrum features</li>
            <li>breast cancer surveillance</li>
            <li>DEXA scan (to assess bone density)</li>
          </ul></ul>
      `,
      image: "/images/genetics/klinefelter1.png",
    },
    {
      type: "question",
      text: "What is the most common karyotype in Klinefelter syndrome?",
      options: ["46,XY", "47,XXY", "45,X", "47,XYY"],
      correct: 1,
      explanation:
        "The most common karyotype in Klinefelter syndrome is 47,XXY, which accounts for approximately 80% of cases. This karyotype results from the presence of an extra X chromosome.",
    },
    {
      type: "question",
      text: "What is the primary mechanism leading to hypogonadism in Klinefelter syndrome?",
      options: [
        "Increased aromatase activity leading to elevated estrogen levels",
        "Direct inhibition of Leydig cell function by the extra X chromosome",
        "Autoimmune destruction of testicular tissue",
        "Progressive fibrosis of seminiferous tubules leading to loss of Sertoli cell function",
      ],
      correct: 3,
      explanation:
        "The primary mechanism leading to hypogonadism in Klinefelter syndrome is progressive fibrosis of the seminiferous tubules, which leads to the loss of Sertoli cell function. This results in impaired spermatogenesis and contributes to the characteristic small, firm testes seen in affected individuals. While increased aromatase activity and elevated estrogen levels contribute to some clinical features (e.g., gynaecomastia), they are not the primary cause of hypogonadism.",
    },
    {
      type: "question",
      text: "Which of the following is a common neurodevelopmental feature of Klinefelter syndrome?",
      options: [
        "Severe intellectual disability",
        "Normal neurodevelopment with no specific difficulties",
        "Specific difficulties with language-based learning and expressive language",
        "Predominantly motor coordination difficulties",
      ],
      correct: 2,
      explanation:
        "Individuals with Klinefelter syndrome generally have an IQ within the normal range, but they often experience specific difficulties with language-based learning and expressive language. They may also have an increased risk of anxiety, depression, autism spectrum features, and higher rates of ADHD.",
    },
    {
      type: "question",
      text: "What is the most effective fertility preservation strategy for individuals with Klinefelter syndrome?",
      options: [
        "Sperm banking during adulthood",
        "Testicular sperm extraction (TESE) during adolescence",
        "In vitro fertilization (IVF) with donor sperm",
        "No fertility preservation strategy is effective in Klinefelter syndrome",
      ],
      correct: 1,
      explanation:
        "Testicular sperm extraction (TESE) during adolescence is the most effective fertility preservation strategy for individuals with Klinefelter syndrome. This is because focal areas of spermatogenesis may persist in the testes, and performing TESE before complete tubular destruction occurs can increase the chances of retrieving viable sperm for use in assisted reproductive technologies such as intracytoplasmic sperm injection (ICSI). Sperm banking during adulthood is less effective due to azoospermia, and IVF with donor sperm does not preserve the individual's own fertility.",
    },
    {
      type: "question",
      text: "Which of the following is NOT a common clinical feature of Klinefelter syndrome?",
      options: [
        "Tall stature with long limbs",
        "Small, firm testes",
        "Gynaecomastia",
        "Severe intellectual disability",
      ],
      correct: 3,
      explanation:
        "Severe intellectual disability is not a common clinical feature of Klinefelter syndrome. Individuals with Klinefelter syndrome typically have an IQ within the normal range, although they may experience specific difficulties with language-based learning and expressive language. The other options listed (tall stature with long limbs, small firm testes, and gynaecomastia) are common clinical features of Klinefelter syndrome.",
    },
    {
      type: "question",
      text: "What is the primary reason for the increased risk of breast cancer in individuals with Klinefelter syndrome?",
      options: [
        "Increased levels of testosterone",
        "Increased levels of estrogen due to peripheral aromatization",
        "Genetic mutations in the BRCA1 and BRCA2 genes",
        "Exposure to environmental carcinogens",
      ],
      correct: 1,
      explanation:
        "The increased risk of breast cancer in individuals with Klinefelter syndrome is primarily due to increased levels of estrogen resulting from peripheral aromatization of testosterone. The hormonal imbalance, characterized by low testosterone and high estrogen levels, contributes to the development of breast tissue and increases the risk of breast cancer.",
    },
    {
      type: "text",
      title: "Trisomy X (47,XXX) and XYY Syndrome (47,XYY)",
      content: `
     
          <h3>Trisomy X (47,XXX)</h3>
          <ul>
            <li><b>Prevalence:</b> ~1 in 1,000 female births. Arises from nondisjunction, most commonly maternal meiosis I (maternal age effect). <b>Two of the three X chromosomes are inactivated</b>,but some genes escape inactivation and contribute to phenotype</li>
            <li><b>Clinical features:</b> Most are <strong>undiagnosed</strong> as symptoms are generally mild.
            <ul> <li>Tall stature with long legs</li> <li>mild <span class="glossary-term">hypotonia<span class="tooltip">abnormally low muscle tone</span></span></li> 
            <li>mild learning difficulties particularly affecting speech and language development (average IQ 85–90, approximately 10–15 points below siblings)</li>
            <li>clinodactyly (curvature of the fingers)</li>
             <li>hyperextensible joints</li>
             <li>mild dysmorphic features in some (hypertelorism, epicanthal folds, upslanting palpebral fissures)</li>
            </ul>
            <li><span class="purple-term">Fertility and puberty</span>: Fertility is generally normal, though there is an increased risk of premature ovarian insufficiency (POI). Average age at menopause is approximately 47 years (several years earlier than the general population average of ~51 years). Regular gynaecological review is reasonable</li>
            <li><b>Management:</b> Neurodevelopmental evaluation to identify educational support needs; speech and language therapy if indicated; psychological support. No specific medical treatment required</li>
          </ul>
      `,
    },
    {
      type: "question",
      text: "How common is Trisomy X (47,XXX)?",
      options: [
        "1 in 100 female births",
        "1 in 1,000 female births",
        "1 in 10,000 female births",
        "1 in 100,000 female births",
      ],
      correct: 1,
      explanation:
        "Trisomy X (47,XXX) occurs in approximately 1 in 1,000 female births. It arises from nondisjunction during meiosis, most commonly maternal meiosis I, and is associated with a maternal age effect.",
    },
    {
      type: "question",
      text: "What is the typical clinical presentation of individuals with Trisomy X (47,XXX)?",
      options: [
        "Severe intellectual disability and multiple congenital anomalies",
        "Mild learning difficulties, tall stature, and normal fertility",
        "Normal intelligence and no physical features",
        "Short stature and infertility",
      ],
      correct: 1,
      explanation:
        "Individuals with Trisomy X (47,XXX) typically present with mild learning difficulties, tall stature, and normal fertility. Most are undiagnosed due to the generally mild symptoms. They may also have mild hypotonia, clinodactyly, hyperextensible joints, and mild dysmorphic features in some cases.",
    },
    {
      type: "text",
      title: "XYY Syndrome (47,XYY)",
      content: `     
          <h3>XYY Syndrome (47,XYY)</h3>
          <ul>
            <li><b>Prevalence:</b> ~1 in 1,000 male births. Arises from <strong>paternal nondisjunction in meiosis II</strong> (the extra Y is always paternal in origin — maternal age is not a risk factor). The extra Y chromosome is not inactivated (unlike the extra X in Klinefelter)</li>
            <li><b>Clinical features:</b> Most are <strong>undiagnosed</strong>
            <ul>
            <li>Tall stature (often >75th centile)</li> 
            <li>severe acne in adolescence</li>
            <li>mild learning difficulties affecting speech and language development (average IQ approximately 10–15 points below siblings, within normal range)</li>
            <li>possible attention difficulties and impulsivity. The historical association with antisocial or criminal behaviour has been largely refuted in population studies — most XYY men have no behavioural problems</li>
            </ul>
            <li><span class="purple-term">Fertility and development</span>: Sexual development and fertility are <strong>usually normal</strong>. Testosterone levels are normal. Unlike Klinefelter, azoospermia is not expected. Most men with 47,XYY are never diagnosed</li>
            <li><b>Diagnosis:</b> Karyotype, FISH, or microarray. Often detected incidentally on prenatal testing</li>
            <li><b>Management:</b> Speech and language therapy if needed; educational support; psychological support. Routine genetic counselling for couples who have a 47,XYY fetus detected prenatally. The prognosis is generally good and most live essentially normal lives</li>
          </ul>
      
        `,
    },
    {
      type: "question",
      text: "What are the typical clinical features of XYY Syndrome (47,XYY)?",
      options: [
        "Short stature, infertility, and severe intellectual disability",
        "Normal stature, normal fertility, and no learning difficulties",
        "Tall stature, severe acne, and mild learning difficulties",
        "Short stature, normal fertility, and mild learning difficulties",
      ],
      correct: 2,
      explanation:
        "XYY Syndrome (47,XYY) is typically characterized by tall stature (often above the 75th percentile), severe acne during adolescence, and mild learning difficulties affecting speech and language development. Most individuals with XYY Syndrome have normal sexual development and fertility. The historical association with antisocial or criminal behavior has been largely refuted in population studies.",
    },
    {
      type: "question",
      text: "What is the prevalence of XYY Syndrome (47,XYY)?",
      options: [
        "1 in 100 male births",
        "1 in 1,000 male births",
        "1 in 10,000 male births",
        "1 in 100,000 male births",
      ],
      correct: 1,
      explanation:
        "XYY Syndrome (47,XYY) occurs in approximately 1 in 1,000 male births.",
    },
  ],
};
